import { RxDocument } from 'rxdb'
import { LOGGER } from '@cicero/data/utils'
import { GET_SYNCABLE_MODELS, REMOVE_DOC } from 'models/helpers'
import { IAction, IActionHandler, ModelService, Store } from 'models/services'

export class SYNC_MODELS_OVER_HTTP implements IActionHandler<ISyncEventsPayload, any> {
  async exec(action: IAction<ISyncEventsPayload>, store: Store) {
    const syncable = GET_SYNCABLE_MODELS(store)

    // Determine individual rows with unsynced changes
    let syncItemsCount = 0
    const syncItems: { [modelName: string]: { docs: RxDocument<any>[], model: ModelService<any> } } = {}
    for (const m of syncable) {
      const coll = await m.GET_COLLECTION()
      const unsynced = await m.schema.LIFECYCLE.loadSyncQueue(coll)
      if (unsynced.length) {
        syncItemsCount += unsynced.length
        syncItems[m.schema.CONFIG.name_plural] = { docs: unsynced, model: m }
      }
    }

    store.API.unsynced$.next(syncItemsCount)
    if (syncItemsCount === 0) { return { message: '0 items to sync', syncItems } }

    // Parse changes into a request and call the sync API
    const body = Object.entries(syncItems).reduce((acc: any, [modelName, syncAction]) => {
      acc[modelName] = syncAction.docs.map(doc => syncAction.model.schema.LIFECYCLE.preSync(doc, syncAction.model.schema.CONFIG))
      return acc
    }, {})

    const { response, status } = await store.API.POST('/editor/sync', { body })
    let successfulSyncs = syncItemsCount

    // Make changes back to local store
    for (const [modelName, { docs, model }] of Object.entries(syncItems)) {
      const responseEntities = response[modelName]
      const requestEntities = body[modelName]
      if (!responseEntities) {
        LOGGER.warn('[SYNC_MISSING_KEY]', response, modelName)
        break
      }

      const changes = await Promise.all(docs.map(async (doc, i) => {
        const entity = responseEntities[i]

        if (entity.errors) {
          successfulSyncs--
          LOGGER.warn('[SYNC_ERROR]', { request: requestEntities[i], response: entity, doc })
          if (model.schema.LIFECYCLE.afterSyncError) { model.schema.LIFECYCLE.afterSyncError(store, doc, entity) }
          return entity
        }

        if (entity.deleted) {
          const d = REMOVE_DOC(await model.GET_COLLECTION(), doc.id)
          if (model.schema.LIFECYCLE.afterSyncDeletion) { model.schema.LIFECYCLE.afterSyncDeletion(store, doc) }
          return d
        }

        return model.PERSIST(entity, doc.id)
      }))

      if (model.schema.LIFECYCLE.afterSync) { return model.schema.LIFECYCLE.afterSync(store, docs, changes) }
    }

    const erroredSyncs = syncItemsCount - successfulSyncs
    return { message: `${successfulSyncs} entities synced${erroredSyncs ? ` (${erroredSyncs} errors)` : ''}`, syncItems, response, status }
  }
}

interface ISyncEventsPayload {
  endpoint: string
}
